import { Dialog, FormSectionHeader, Loader } from "@/components";
import { Button, Card, CardContent } from "@/components/ui";
import useDownloadTemplate from "./useUploadTemplate";
import { FileChartColumn, Upload } from "lucide-react";

const UploadTemplate = ({currentConfig, isLoading}: {currentConfig: any; isLoading?: boolean}) => {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    file,
    openValidationDialog,
    setOpenValidationDialog,
    onViewMore,
    onSubmit,
    isValidatePending,
    fileErrors,
    onCancelUpload,
    rowsDtl,
    downloadErrorsAsText,
    isUploadPending
  } = useDownloadTemplate({currentConfig});

  return (
    <>
      <Card>
        {isLoading ? <Loader /> : 
          <CardContent>
            <FormSectionHeader className="text-black" wrapperClassName="mb-0">Document Template</FormSectionHeader>
            <span className="text-[12px] text-g-400">Template structure changes based on selected document type and configuration.</span>

            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-10 mt-6 gap-5">
                <div {...getRootProps()} className="lg:col-span-6 border rounded-md px-3 py-8 flex flex-col items-center justify-center gap-3 cursor-pointer">
                  <input {...getInputProps()} />
                  <div className="p-3 bg-blue-50 rounded-full">
                    <FileChartColumn className="stroke-primary h-5 w-5" />
                  </div>
                  <span className="text-[16px] font-semibold">
                    {
                      isDragActive ?
                        <p>Drop the files here ...</p> :
                        <p>Drop file here or browse</p>
                    }
                  </span>
                  <span className="text-[12px] text-slate-500 w-[300px] text-center">Supported format: .xlsx | Template aligned to Manual Journal Entry</span>
                  <Button><Upload /> Upload File</Button>
                </div>
                <div className="lg:col-span-4 bg-primary text-white rounded-md p-3">
                  <FormSectionHeader>Uploaded File</FormSectionHeader>
                  {file ? <div className="flex flex-col gap-3">
                    {file ? 
                      <div className="flex flex-row gap-3 items-center bg-gray-50 rounded-md p-4">
                        <div className="p-3 bg-blue-100 rounded-full">
                          <FileChartColumn className="stroke-primary h-5 w-5" />
                        </div>
                        <div className="text-black flex flex-col gap-1">
                          <span className="text-[14px]">{file?.name || ""}</span>
                          <div className="text-[10px] text-gray-600 flex flex-row gap-1 items-center">
                            {file?.size ? `${(file?.size / (1024 * 1024)).toFixed(2)} MB` : ""} 
                            <div className="h-[3px] w-[3px] bg-gray-500 rounded-full" /> 
                            <span>Uploaded just now</span>
                          </div>
                        </div> 
                      </div> : <></>
                    }
                    {
                      fileErrors?.length ? <>
                      <div className="flex flex-row gap-3 items-center rounded-md border border-dotted p-3 text-[14px] justify-between">
                        <span>Rows Passed</span>
                        <span className="bg-gray-100 rounded-full h-8 w-8 text-black flex items-center justify-center">{String(rowsDtl.current.rowsPassed).padStart(2,"0")}</span>
                      </div>
                      <div className="flex flex-row gap-3 items-center rounded-md border border-dotted p-3 text-[14px] justify-between">
                        <span>Errors Found</span>
                        <span className="bg-gray-100 rounded-full h-8 w-8 text-black flex items-center justify-center">{String(rowsDtl.current.rowsFailed).padStart(2,"0")}</span>
                      </div>
                      </> : <></>
                    }
                  </div> : <div className="flex items-center justify-center h-[70%] text-white/80">No File Uploaded</div>}
                </div>
              </div>

              {fileErrors?.length ? <>
                <div className="flex flex-row flex-wrap gap-3">
                  {fileErrors?.slice(0,10)?.map((data: any, index: number) => 
                    <div className="bg-red-50 border border-red-200 p-4 rounded-md flex flex-col">
                      <span className="text-[14px]">Validation {index + 1}</span>
                      <span className="text-[12px] text-gray-500">
                        Field: {data?.fieldCode} <br/>
                        Error: {data?.errorMessage} <br/>
                        Expected Value: {data?.expectedValue} <br/>
                        {data?.rowNumber ? `Row Number: ${data?.rowNumber}` : ''}
                      </span>
                    </div>
                  )}
                </div>
                {fileErrors?.length > 10 ? <div className="flex items-center justify-end">
                  <Button variant="ghost" className="p-0 text-primary hover:bg-transparent" onClick={onViewMore}>View More</Button>
                </div> : <></>}
              </> : <></>}
            </div>
          </CardContent>
        }
      </Card>
      <div className="flex flex-row items-center justify-end gap-4 mt-5">
        <Button onClick={onSubmit} disabled={isValidatePending || isUploadPending || !currentConfig}>{isValidatePending || isUploadPending ? <Loader variant="secondary" /> : <></>} Submit</Button>
        {fileErrors?.length ? <Button variant="outline"  disabled={isValidatePending || isUploadPending} onClick={downloadErrorsAsText}
          className="bg-transparent hover:bg-transparent hover:text-primary border-primary">Download Error Log</Button> : <></>}
        {file ? <Button variant="outline"  disabled={isValidatePending || isUploadPending} onClick={onCancelUpload}
          className="bg-transparent hover:bg-transparent hover:text-primary border-primary">Cancel Upload</Button> : <></>}
      </div>
      <Dialog
        open={openValidationDialog} 
        setOpen={setOpenValidationDialog}
        title={
          <div className="text-2xl font-semibold text-red-600">
            {"Validation Details"}
            <div className="h-1 w-[30%] bg-red-600 rounded-full" />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          {fileErrors?.map((data: any, index: number) => 
            <div className="bg-red-50 border border-red-200 p-4 rounded-md flex flex-col">
              <span className="text-[14px]">Validation {index + 1}</span>
              <span className="text-[12px] text-gray-500">
                Field: {data?.fieldCode} <br/>
                Error: {data?.errorMessage} <br/>
                Expected Value: {data?.expectedValue} <br/>
                {data?.rowNumber ? `Row Number: ${data?.rowNumber}` : ''}
              </span>
            </div>
          )}
        </div>
      </Dialog>
    </>
  )
}

export default UploadTemplate;